/**
 * 微信支付中台对接（落地页模式）
 * 本系统标识: aigc（商户订单号与 attach 均使用此前缀）。
 * 下游不调用中台任何接口：仅携带下单参数重定向到落地页，落地页内获取用户信息、下单、支付。
 * 支付后跳回本页；轮询中台订单状态，轮询成功后再执行本项目的 Supabase 逻辑。
 */

/** 支付中台地址（与落地页、订单状态查询同域，仅 path 不同）；HTTPS 页面必须用 HTTPS，否则混合内容会被浏览器阻止 */
const PAY_CENTER_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PAY_CENTER_URL) || 'https://pay.jiuanci.com';
const _base = PAY_CENTER_BASE.replace(/\/$/, '');

/** 中台落地页地址（见下游系统接入说明） */
const PAY_LANDING_URL = `${_base}/pay`;

/** 订单状态查询接口：与支付中台同域，path 为 /api/order/status，中台内部调微信「商户订单号查询订单」 */
const ORDER_STATUS_API_BASE = `${_base}/api/order/status`;

export const WECHAT_PAY_SYSTEM_ID = 'aigc';

/** 生成商户订单号：本系统名称_订单号，6-32 位 */
export function buildOutTradeNo(orderId: string): string {
  const raw = `${WECHAT_PAY_SYSTEM_ID}_${orderId}`.replace(/\s/g, '');
  return raw.length > 32 ? raw.slice(0, 32) : raw;
}

/** 生成落地页 URL：下游仅拼链并跳转/展示二维码，不调中台接口；落地页内读 URL 参数并完成获取用户、下单、支付（含业务系统 user_id，见下游系统接入说明） */
export function buildPayLandingUrl(params: {
  outTradeNo: string;
  totalCents: number;
  description: string;
  userId: string;
  redirect?: string;
  cancel?: string;
}): string {
  const q = new URLSearchParams();
  q.set('out_trade_no', params.outTradeNo);
  q.set('attach', WECHAT_PAY_SYSTEM_ID);
  q.set('total', String(params.totalCents));
  q.set('description', params.description);
  q.set('user_id', params.userId);
  if (params.redirect != null && params.redirect !== '') q.set('redirect', params.redirect);
  if (params.cancel != null && params.cancel !== '') q.set('cancel', params.cancel);
  return `${PAY_LANDING_URL}?${q.toString()}`;
}

/** JSAPI 下单（落地页模式）— 中台返回 pay_url，下游跳转至落地页完成调起 */
export interface JSAPIPrepayParams {
  description: string;
  out_trade_no: string;
  totalCents: number;
  openid: string;
}

export interface JSAPIPrepayResult {
  appId: string;
  timeStamp: string;
  nonceStr: string;
  package: string;
  signType: string;
  paySign: string;
}

/** 中台返回的落地页信息（优先使用 pay_url 跳转） */
export interface JsapiPrepayLandingResult extends JSAPIPrepayResult {
  pay_url?: string;
  pay_token?: string;
}

export async function jsapiPrepay(
  params: JSAPIPrepayParams
): Promise<{ ok: boolean; data?: JsapiPrepayLandingResult; error?: string }> {
  try {
    const body = {
      description: params.description,
      out_trade_no: params.out_trade_no,
      amount: { total: params.totalCents, currency: 'CNY' as const },
      payer: { openid: params.openid },
      attach: WECHAT_PAY_SYSTEM_ID,
    };
    const res = await fetch(`${_base}/api/jsapi/prepay`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: (data as any).message || data?.code || `HTTP ${res.status}` };
    }
    const pay: JsapiPrepayLandingResult = {
      appId: (data as any).appId,
      timeStamp: (data as any).timeStamp,
      nonceStr: (data as any).nonceStr,
      package: (data as any).package,
      signType: (data as any).signType || 'RSA',
      paySign: (data as any).paySign,
      pay_url: (data as any).pay_url,
      pay_token: (data as any).pay_token,
    };
    if (!pay.appId || !pay.paySign) return { ok: false, error: '未返回完整调起参数' };
    return { ok: true, data: pay };
  } catch (e: any) {
    return { ok: false, error: e?.message || '网络错误' };
  }
}

/** 订单状态查询结果（与微信「商户订单号查询订单」应答字段一致） */
export interface OrderStatusResult {
  trade_state: 'SUCCESS' | 'NOTPAY' | string;
  transaction_id?: string;
}

/**
 * 根据商户订单号查询订单状态（轮询用）。
 * 中台约定：请求到达并完成处理一律返回 HTTP 200，下游只看 200 + body.code。
 * code: 'SUCCESS' 时带 trade_state、transaction_id；其余为 ORDER_NOT_EXIST / MISSING_OUT_TRADE_NO / QUERY_FAILED / ERROR 等，见实施方案。
 */
export async function queryOrderStatus(outTradeNo: string): Promise<{ ok: boolean; data?: OrderStatusResult; error?: string }> {
  try {
    const url = `${ORDER_STATUS_API_BASE}${ORDER_STATUS_API_BASE.includes('?') ? '&' : '?'}out_trade_no=${encodeURIComponent(outTradeNo)}`;
    const res = await fetch(url);
    const data = await res.json().catch(() => ({})) as { code?: string; message?: string; trade_state?: string; transaction_id?: string };
    if (!res.ok) {
      return { ok: false, error: data.message || data.code || `HTTP ${res.status}` };
    }
    if (data.code === 'SUCCESS') {
      const trade_state = data.trade_state ?? 'NOTPAY';
      const transaction_id = data.transaction_id;
      return { ok: true, data: { trade_state, transaction_id } };
    }
    return { ok: false, error: data.message || data.code || '查询未成功' };
  } catch (e: any) {
    return { ok: false, error: e?.message || '网络错误' };
  }
}

/** 微信申请退款：POST {中台基地址}/api/wx/refund，中台调微信 V3 退款 API */
export interface WxRefundParams {
  outTradeNo: string;
  outRefundNo: string;
  refund: number;
  total: number;
  reason?: string;
}

export async function requestWxRefund(
  params: WxRefundParams
): Promise<{ ok: boolean; data?: Record<string, unknown>; error?: string }> {
  try {
    const body = {
      out_trade_no: params.outTradeNo,
      out_refund_no: params.outRefundNo,
      refund: params.refund,
      total: params.total,
      attach: WECHAT_PAY_SYSTEM_ID,
      ...(params.reason != null && params.reason !== '' && { reason: params.reason }),
    };
    const res = await fetch(`${_base}/api/wx/refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({})) as { code?: string; message?: string; data?: Record<string, unknown> };
    if (!res.ok) {
      return { ok: false, error: data.message || data.code || `HTTP ${res.status}` };
    }
    if (data.code === 'SUCCESS') {
      return { ok: true, data: data.data };
    }
    return { ok: false, error: data.message || data.code || '退款申请未成功' };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : '网络错误';
    return { ok: false, error: msg };
  }
}

/** 调起微信内支付（仅在微信内置浏览器中调用） */
declare global {
  interface Window {
    WeixinJSBridge?: {
      invoke: (method: string, params: Record<string, string>, cb: (err: { err_msg?: string }) => void) => void;
    };
  }
}

export function invokeWechatPay(
  params: JSAPIPrepayResult
): Promise<{ success: boolean; errMsg?: string }> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.WeixinJSBridge) {
      resolve({ success: false, errMsg: '非微信环境' });
      return;
    }
    window.WeixinJSBridge!.invoke(
      'getBrandWCPayRequest',
      {
        appId: params.appId,
        timeStamp: params.timeStamp,
        nonceStr: params.nonceStr,
        package: params.package,
        signType: params.signType,
        paySign: params.paySign,
      },
      (err: { err_msg?: string }) => {
        if (err && err.err_msg && err.err_msg !== 'get_brand_wcpay_request:ok') {
          resolve({ success: false, errMsg: err.err_msg });
        } else {
          resolve({ success: true });
        }
      }
    );
  });
}
